import { useState, useMemo, useRef, useEffect, useCallback } from 'react';

import { usePlantillas } from '@/api/usePresentationWizard';
import { Input } from '@/components/forms';
import { TemplateCard, StepShell } from '../components';
import styles from './SelectTemplate.module.css';

const TAG_LABELS: Record<string, string> = {
  corporativo: 'Corporate',
  general: 'General',
  navidad: 'Christmas',
  festividad: 'Holiday',
  aventura: 'Adventure',
  outdoor: 'Outdoor',
  naturaleza: 'Nature',
  circo: 'Circus',
  diversion: 'Fun',
  tematico: 'Themed',
  musica: 'Music',
  concierto: 'Concert',
  entretenimiento: 'Entertainment',
  deportes: 'Sports',
  competencia: 'Competition',
  olimpiadas: 'Olympics',
  'dia-de-muertos': 'Day of the Dead',
  tradicion: 'Tradition',
  'dia-del-padre': "Father's Day",
  familia: 'Family',
  disco: 'Disco',
  fiesta: 'Party',
  retro: 'Retro',
  neon: 'Neon',
  'dia-de-la-madre': "Mother's Day",
  'ano-nuevo': 'New Year',
  celebracion: 'Celebration',
  oktoberfest: 'Oktoberfest',
  cerveza: 'Beer',
  tecnologia: 'Technology',
  innovacion: 'Innovation',
};

function formatTag(tag: string): string {
  return TAG_LABELS[tag] || tag.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

interface Props {
  selected: string;
  onSelect: (slug: string) => void;
}

export const SelectTemplate = ({ selected, onSelect }: Props) => {
  const { data: plantillas, isLoading } = usePlantillas();
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({ isDown: false, startX: 0, scrollLeft: 0, hasMoved: false });

  const allTags = useMemo(() => {
    if (!plantillas) return [];
    const tagSet = new Set<string>();
    for (const p of plantillas) {
      for (const t of p.tags) tagSet.add(t);
    }
    return Array.from(tagSet).sort();
  }, [plantillas]);

  const filtered = useMemo(() => {
    if (!plantillas) return [];
    return plantillas.filter((p) => {
      const matchesSearch =
        !search ||
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.description.toLowerCase().includes(search.toLowerCase());
      const matchesTag = !activeTag || p.tags.includes(activeTag);
      return matchesSearch && matchesTag;
    });
  }, [plantillas, search, activeTag]);

  // Wheel → horizontal scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Drag-to-scroll via mouse events (not pointer capture, so clicks still work)
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    dragState.current = {
      isDown: true,
      startX: e.clientX,
      scrollLeft: el.scrollLeft,
      hasMoved: false,
    };
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragState.current.isDown) return;
      const dx = e.clientX - dragState.current.startX;
      if (Math.abs(dx) > 5) dragState.current.hasMoved = true;
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = dragState.current.scrollLeft - dx;
      }
    };
    const onMouseUp = () => {
      dragState.current.isDown = false;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleCardClick = useCallback(
    (slug: string) => {
      if (!dragState.current.hasMoved) {
        onSelect(slug);
      }
    },
    [onSelect],
  );

  return (
    <StepShell
      title="Select a Template"
      subtitle="Choose the visual style for your presentation"
      loading={isLoading}
      loadingMessage="Loading templates..."
    >
      <div className={styles.searchRow}>
        <Input
          name="search"
          value={search}
          setValue={setSearch}
          placeholder="Search templates..."
          size="md"
        />
      </div>

      <div className={styles.tagsRow}>
        <button
          className={`${styles.tagChip} ${!activeTag ? styles.tagActive : ''}`}
          onClick={() => setActiveTag(null)}
        >
          All
        </button>
        {allTags.map((tag) => (
          <button
            key={tag}
            className={`${styles.tagChip} ${activeTag === tag ? styles.tagActive : ''}`}
            onClick={() => setActiveTag((prev) => (prev === tag ? null : tag))}
          >
            {formatTag(tag)}
          </button>
        ))}
      </div>

      <div
        className={`${styles.scrollContainer} ${dragState.current.isDown ? styles.dragging : ''}`}
        ref={scrollRef}
        onMouseDown={onMouseDown}
      >
        <div className={styles.scrollTrack}>
          {filtered.length > 0 ? (
            filtered.map((p) => (
              <TemplateCard
                key={p.slug}
                plantilla={p}
                isSelected={selected === p.slug}
                onClick={() => handleCardClick(p.slug)}
              />
            ))
          ) : (
            <div className={styles.empty}>No templates found</div>
          )}
        </div>
      </div>

      {filtered.length > 0 && (
        <p className={styles.hint}>
          {filtered.length} template{filtered.length !== 1 ? 's' : ''} available — scroll
          horizontally to see more
        </p>
      )}
    </StepShell>
  );
};
